import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import clsx from 'clsx'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { RxCross2 } from 'react-icons/rx'

import { useGetPostByIdQuery } from '../../services/apiSlice'
import Spinner from '../../components/loading/Spinner'
import NotFoundMedia from '../../components/fallback/NotFoundMedia'

const PostMedia: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  if (location.search === '') {
    return <NotFoundMedia />
  }

  const searchParts = location.search.split('&')

  if (!searchParts[0] || !searchParts[1] || !searchParts[2]) {
    return <NotFoundMedia />
  }

  const username = searchParts[0].split('=')[1]
  const id = searchParts[1].split('=')[1]
  let slide: number = Number(searchParts[2].split('=')[1])
  slide = Number.isNaN(slide) ? 1 : slide
  slide = slide > 2 ? 2 : slide < 1 ? 1 : slide

  const [mediaSlideDisplayed, setMediaSlideDisplayed] = useState<{
    slide: number
    dualMedia: boolean
  }>({
    slide: slide,
    dualMedia: false,
  })

  const { data, isSuccess, isLoading, isError } = useGetPostByIdQuery(
    {
      postId: id,
      username: username,
    },
    { skip: !username || !id },
  )

  if (isLoading) {
    return (
      <div className="bgd-theme flex h-dvh w-dvw items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!data && isError) {
    return <NotFoundMedia />
  }

  let mediaDisplay = <></>

  if (data && isSuccess) {
    const media = data?.postMedia ? data.postMedia.split(',') : null
    const mediaTypes = data?.mediaTypes ? data.mediaTypes.split(',') : null

    if (media && mediaTypes) {
      let mediaImages: string[] = []
      mediaTypes?.forEach((m, idx) => {
        if (m === 'image') {
          mediaImages.push(media[idx])
        }
      })

      if (mediaImages.length === 0) {
        // we only want to display images on this route
        // this won't happen normally because you can't click video element, but if you manually input in url media?id={postId}, route will be display regardless of if you have all video medias... therefore we need this fallback
        mediaDisplay = (
          <div className="text-2xl">
            Please go back to{' '}
            <button
              type="button"
              className="text-blue cursor-pointer underline"
              onClick={() => {
                navigate(-1)
              }}
            >
              post
            </button>
          </div>
        )
      } else {
        const display = mediaImages.map((m, idx) => {
          const inline = {
            backgroundImage: 'url(' + m + ')',
          }
          return (
            <div
              key={m}
              className={clsx(
                'absolute flex h-full w-full shrink-0 grow items-center justify-center object-contain transition-all duration-500 ease-in-out',
                {
                  'translate-x-0': mediaSlideDisplayed.slide === idx + 1,
                  'translate-x-full':
                    mediaImages.length > 1 &&
                    mediaSlideDisplayed.slide < idx + 1,
                  '-translate-x-full':
                    mediaImages.length > 1 &&
                    mediaSlideDisplayed.slide > idx + 1,
                },
              )}
            >
              <div className="relative h-full w-full">
                <div
                  style={inline}
                  className="absolute inset-x-0 inset-y-0 h-full w-full min-w-0 rounded-[30px] bg-contain bg-center bg-no-repeat"
                />
                <img
                  className="absolute inset-x-0 inset-y-0 h-full w-full min-w-0 rounded-[30px] opacity-0"
                  src={m}
                  alt={m}
                />
              </div>
            </div>
          )
        })
        mediaDisplay = <>{display}</>
      }
    } else {
      mediaDisplay = (
        <div className="text-2xl">
          Please go back to{' '}
          <button
            type="button"
            className="text-blue cursor-pointer underline"
            onClick={() => {
              navigate(-1)
            }}
          >
            post
          </button>
        </div>
      )
    }

    return (
      <div className="bgd-theme fixed flex h-dvh w-dvw items-center justify-center">
        <div className="relative flex h-dvh w-[10%] items-center justify-center text-2xl">
          <button
            type="button"
            className="hover-sub absolute top-[10px] left-[10px] flex cursor-pointer items-center justify-center rounded-full p-[10px] text-3xl"
            onClick={() => {
              navigate(`/${data?.username}/${data?.postId}`)
            }}
          >
            <RxCross2 />
          </button>
          <button
            type="button"
            className="hover-sub flex cursor-pointer items-center justify-center rounded-full p-[20px]"
            onClick={() => {
              setMediaSlideDisplayed((curr) => {
                return { ...curr, slide: 1 }
              })
            }}
          >
            <FaChevronLeft />
          </button>
        </div>
        <div className="relative flex h-dvh w-[90%] overflow-hidden">
          <div className="relative inset-y-0 flex h-full w-full">
            {mediaDisplay}
          </div>
        </div>
        <div className="flex h-dvh w-[10%] items-center justify-center text-2xl">
          <button
            type="button"
            className="hover-sub flex cursor-pointer items-center justify-center rounded-full p-[20px]"
            onClick={() => {
              setMediaSlideDisplayed((curr) => {
                return { ...curr, slide: 2 }
              })
            }}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
    )
  }
}

export default PostMedia
